import { Input } from '@1trading/react';
import { X } from 'lucide-react';
import { type KeyboardEvent, useState } from 'react';
import { IP_MIN_LENGTH } from '@/services/api/fiu-utr';

const IP_TOO_SHORT_MESSAGE = `IP entries must be at least ${IP_MIN_LENGTH} characters`;

interface IpChipsInputProps {
  id?: string;
  value: string[];
  onChange: (next: string[]) => void;
}

/** Free-entry multi-chip input (the legacy combobox): Enter commits a chip, chips are closable. */
export const IpChipsInput = ({ id, value, onChange }: IpChipsInputProps) => {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const commitDraft = () => {
    const entry = draft.trim();
    if (!entry) return;
    // Fix: the legacy combobox validated nothing, so a 1–2-char chip 400'd the
    // generate call with a raw zod message — reject short entries at the input instead.
    if (entry.length < IP_MIN_LENGTH) {
      setError(IP_TOO_SHORT_MESSAGE);
      return;
    }
    if (!value.includes(entry)) onChange([...value, entry]);
    setDraft('');
    setError(null);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Enter adds a chip; never submit a surrounding form
      event.preventDefault();
      commitDraft();
    } else if (event.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {value.map((ip) => (
            <span className="inline-flex items-center gap-1 rounded-full border bg-muted px-2.5 py-0.5 font-mono text-xs" key={ip}>
              {ip}
              <button
                aria-label={`Remove ${ip}`}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => onChange(value.filter((existing) => existing !== ip))}
                type="button"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <Input
        id={id}
        onChange={(event) => {
          setDraft(event.target.value);
          setError(null);
        }}
        onKeyDown={handleKeyDown}
        placeholder="e.g. 203.0.113.7 — press enter to add"
        value={draft}
      />
      {error ? <p className="text-sm text-red-500">{error}</p> : null}
    </div>
  );
};
IpChipsInput.displayName = 'IpChipsInput';
